import React, { useState, useEffect, useMemo } from 'react';
import GameCanvas from './components/GameCanvas';
import LandingPage from './components/LandingPage';
import Joystick from './components/Joystick';
import SettingsMenu from './components/SettingsMenu';
import GameOverPopup from './components/GameOverPopup';
import WaitingForOperatives from './components/WaitingForOperatives';
import './App.css';

// Solana Wallet Adapter Imports
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
// Import diretto dell'adapter Phantom invece di @solana/wallet-adapter-wallets:
// quel pacchetto tirava dentro 43 wallet (Trezor, Torus, WalletConnect...) che non
// usiamo, con tutte le loro dipendenze vulnerabili e il loro peso nel bundle.
import { PhantomWalletAdapter } from '@solana/wallet-adapter-phantom';
// Mobile Wallet Adapter: la via ufficiale di Solana per collegarsi da telefono
// su Android. Non passa dai deeplink di Phantom — quelli rifiutano la firma con
// `-32603` (vedi lib/phantomDeeplink.js) — ma da un protocollo che il sistema
// operativo gestisce direttamente: il gioco resta in Chrome, Phantom si apre
// come app, firma, e riporta indietro. Su iOS non esiste, e lì l'adapter
// semplicemente non si presenta come disponibile.
import {
  SolanaMobileWalletAdapter,
  createDefaultAddressSelector,
  createDefaultAuthorizationResultCache,
  createDefaultWalletNotFoundHandler,
} from '@solana-mobile/wallet-adapter-mobile';
import { clusterApiUrl } from '@solana/web3.js';
import '@solana/wallet-adapter-react-ui/styles.css';
import { fetchChainConfig } from './lib/depositClient';
import { isMobile, hasInjectedProvider } from './lib/phantomMobile';

function App() {
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  // L'intro cinematica e' stata rimossa: la vetrina vive sul sito esterno, qui si
  // parte direttamente dalla staging area (wallet, tier, skin) e si entra in partita.
  const [viewMode, setViewMode] = useState('LANDING'); // 'LANDING' | 'GAME'
  const [activeWallet, setActiveWallet] = useState(null);
  const [activeTier, setActiveTier] = useState(1);
  const [activeSkin, setActiveSkin] = useState('#00FF00');
  // Ticket d'ingresso emesso dal server: e' lui a stabilire il tier della
  // partita, non piu' il numero che il client si porta dietro.
  const [activeTicket, setActiveTicket] = useState(null);

  // Su quale rete Solana lavorare. NON va scritto qui dentro: lo decide il
  // server (SOLANA_CLUSTER) e il client lo chiede.
  //
  // Il motivo e' concreto: se il client parlasse con mainnet e il server
  // cercasse le transazioni su devnet, un deposito partirebbe con soldi veri e
  // il server non lo troverebbe mai. Nessun errore, nessun avviso: soldi usciti
  // e mai accreditati. Tenere il valore in un posto solo rende quel disallineo
  // impossibile.
  const [network, setNetwork] = useState(null);

  useEffect(() => {
    let annullato = false;
    fetchChainConfig()
      .then((c) => { if (!annullato) setNetwork(c.cluster); })
      // Server irraggiungibile: si ripiega su devnet, che e' la rete dove non
      // si puo' perdere niente. Il resto dell'app segnalera' comunque il guasto.
      .catch(() => { if (!annullato) setNetwork('devnet'); });
    return () => { annullato = true; };
  }, []);

  const endpoint = useMemo(() => clusterApiUrl(network || 'devnet'), [network]);
  // L'elenco dipende dalla rete, quindi si ricostruisce quando la rete arriva
  // dal server: il Mobile Wallet Adapter autorizza la sessione PER UN CLUSTER, e
  // autorizzarla su quello sbagliato porterebbe a firmare transazioni che il
  // server non troverebbe mai — lo stesso disallineo che il commento su
  // `network` qui sopra esiste per impedire.
  //
  // Su telefono l'adapter dell'ESTENSIONE va tolto dall'elenco. Non e' una
  // pulizia estetica: le estensioni sul telefono non esistono, quindi quella
  // voce non puo' collegare niente e l'unica cosa che sa fare e' mandare alla
  // pagina di download — anche a chi Phantom ce l'ha gia' installato. E' il
  // difetto da cui siamo partiti, e lasciarlo accanto alla voce buona
  // significa offrire due strade di cui una porta contro un muro.
  //
  // L'eccezione e' il browser interno di Phantom, dove il provider VIENE
  // iniettato: li' quell'adapter funziona ed e' l'unica strada per chi sta su
  // iPhone. Da qui la condizione sul provider e non sul solo telefono.
  const wallets = useMemo(() => {
    const soloMobileAdapter = isMobile() && !hasInjectedProvider();
    return [
      ...(soloMobileAdapter ? [] : [new PhantomWalletAdapter()]),
      new SolanaMobileWalletAdapter({
        addressSelector: createDefaultAddressSelector(),
        // Identità mostrata da Phantom nella schermata di autorizzazione.
        // `icon` è un percorso relativo a `uri`, non un indirizzo assoluto.
        appIdentity: {
          name: 'VEXEL',
          uri: window.location.origin,
          icon: 'logo192.png',
        },
        authorizationResultCache: createDefaultAuthorizationResultCache(),
        cluster: network || 'devnet',
        onWalletNotFound: createDefaultWalletNotFoundHandler(),
      }),
    ];
  }, [network]);

  // Imposta style per non permettere scroll su mobile
  useEffect(() => {
    if (viewMode === 'GAME') {
      document.body.style.touchAction = 'none';
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.touchAction = 'auto';
      document.body.style.overflow = 'auto';
    }
  }, [viewMode]);

  // Listener per tornare alla schermata principale senza ricaricare
  useEffect(() => {
    const handleReturnToLanding = () => {
      setViewMode('LANDING');
    };
    window.addEventListener('return_to_landing', handleReturnToLanding);
    return () => window.removeEventListener('return_to_landing', handleReturnToLanding);
  }, []);

  // Gestione Classe Body per il Tema CSS
  useEffect(() => {
    if (isWalletConnected) {
      document.body.classList.add('theme-toxic');
    } else {
      document.body.classList.remove('theme-toxic');
    }
  }, [isWalletConnected]);

  const handleEnterGame = (walletAddress, tier = 1, skin = '#00FF00', ticketId = null) => {
    setActiveWallet(walletAddress);
    setActiveTier(tier);
    setActiveSkin(skin);
    setActiveTicket(ticketId);
    setIsWalletConnected(true);
    setViewMode('GAME');
  };

  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider wallets={wallets} autoConnect>
        <WalletModalProvider>
          <>
            <div className="portrait-lock">
              <h2>⚠️ ROTATE PHONE</h2>
              <p>VEXEL can only be played in Landscape Mode.</p>
            </div>

            {viewMode === 'LANDING' && (
              <LandingPage onEnterGame={handleEnterGame} />
            )}

            {viewMode === 'GAME' && (
              <div className="App">
                <GameCanvas wallet={activeWallet} tier={activeTier} skin={activeSkin} ticketId={activeTicket} />

                {/* Comandi da telefono: si disegnano da soli solo su schermi
                    touch, e parlano con la scena via eventi su `window`
                    (`joystick_move`, `joystick_boost`). Il pannello e' sopra il
                    canvas ma trasparente ai tocchi tranne che sui due comandi,
                    cosi' leaderboard, cash out e rotellina restano cliccabili.
                    Nota: la vecchia idea di ruotare il contenuto via CSS per
                    giocare dentro il browser di Phantom e' stata abbandonata
                    (ROADMAP §5d) — qui non c'e' nessuna rotazione di mezzo. */}
                <Joystick />

                {/* Schermata di fine partita: vive dentro la vista GAME, quindi
                    esce di scena insieme alla partita. Prima era un nodo HTML
                    fuori da React e capitava di vederselo ricomparire sopra la
                    lobby con i dati di una morte precedente. */}
                <GameOverPopup />

                {/* Sala d'attesa: compare se la stanza non ha ancora abbastanza
                    giocatori per far partire la partita. */}
                <WaitingForOperatives />

                {/* Menu impostazioni audio (rotellina in alto a destra) */}
                <SettingsMenu />
              </div>
            )}
          </>
        </WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
}

export default App;